import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useQueryClient, useInfiniteQuery, InfiniteData, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { 
  useTaskDetailsQuery 
} from 'service/project/hooks/workItem';
import { useCreateTaskMutation } from 'service/project/hooks/useCreateTaskMutation';

import { getWorkItemChildren, useDeleteWorkItemMutation } from 'service/project/api/workItem';
import { Task, GetTasksSuccessResponse } from 'types/project/tasks';
import { useProjectDefaultQuery } from 'service/project';
import { useAppSelector } from 'store';
import { selectAuthUser } from 'store/slices/auth/auth.selector';

export interface ChildItemDraft {
  id: string;
  workItemUuid?: string;
  title: string;
  workItemKey: string;
  workStatusUuid: string;
  workStatusName: string;
  workStatusCategory: string;
  workTypeUuid: string | null;
  workTypeCategory: string | null;
  assigneeUuid: string | null;
  reporterUuid: string | null;
  priority: string;
  createdDate: string;
  lastModifiedAt: string;
  displayOrder: number;
  totalSubTasksCount: number;
  totalChildWorkItemsCount: number;
  totalCommentsCount: number;
  totalMediaCount: number;
  totalLoggedTime: string;
  totalLoggedMinutes: number;
  childTotalElements?: number;
  childWorkItems?: ChildItemDraft[];
  // This item's own listVersion, for its direct children — required as
  // expectedListVersion when reordering one of them.
  listVersion?: number;
}

export interface LinkedItemDraft {
  id: string;
  targetWorkItemUuid: string;
  sourceWorkItemUuid: string;
  linkedWorkItemUuid: string;
  linkType: string;
  resolvedTask?: any;
}

export interface AttachmentDraft {
  id: string;
  mediaUuid?: string;
  name: string;
  size: number;
  type: string;
  url: string;
  file?: File;
  status: 'ready' | 'uploading' | 'error';
}

export const useTaskDetailManagement = ({
  projectId,
  taskId,
  isEditMode,
  enabled,
  filters,
}: {
  projectId: string;
  taskId: string | null;
  isEditMode: boolean;
  enabled?: boolean;
  filters?: any;
}) => {
  const queryClient = useQueryClient();
  const authUser = useAppSelector(selectAuthUser);
  const { data: defaultData } = useProjectDefaultQuery(projectId);

  const { mutate: childItemMutation } = useCreateTaskMutation();
  const { mutate: deleteTask } = useDeleteWorkItemMutation();

  const [localAttachments, setLocalAttachments] = useState<AttachmentDraft[]>([]);
  const [localLinkedItems, setLocalLinkedItems] = useState<LinkedItemDraft[]>([]);
  const [pageSizeLimit, setPageSizeLimit] = useState(20);

  useEffect(() => {
    setPageSizeLimit(20);
  }, [taskId]);

  // Use a ref to keep track of the latest localAttachments for unmount cleanup
  const localAttachmentsRef = useRef<AttachmentDraft[]>([]);
  useEffect(() => {
    localAttachmentsRef.current = localAttachments;
  }, [localAttachments]);

  // Cleanup object URLs to prevent memory leaks ONLY on unmount
  useEffect(() => {
    return () => {
      localAttachmentsRef.current.forEach((attachment: any) => {
        if (attachment.url && attachment.url.startsWith("blob:")) {
          URL.revokeObjectURL(attachment.url);
        }
      });
    };
  }, []);

  // --- Queries ---
  const { data: existingTask, isLoading: isFetchingTask } = useTaskDetailsQuery(
    projectId,
    taskId ?? undefined,
    0,
    0,
    false,
    { enabled: enabled !== false && !!taskId }
  );

  // Infinite Query for Children
  const {
    data: childrenInfiniteData,
    fetchNextPage: fetchNextChildPage,
    hasNextPage: hasNextChildPage,
    isFetchingNextPage: isFetchingMoreChildren,
    isLoading: isLoadingInitialChildren,
  } = useInfiniteQuery<any, Error, InfiniteData<any>>({
    queryKey: ['task-children-infinite', projectId, taskId, pageSizeLimit, filters],
    queryFn: async ({ pageParam = null }) => {
      try {
        const response = await getWorkItemChildren(projectId, taskId!, pageParam as string | null, pageSizeLimit, filters);
        return response;
      } catch (error: any) {
        if (error?.status === 400 && pageParam !== null) {
          queryClient.resetQueries({ queryKey: ['task-children-infinite', projectId, taskId, pageSizeLimit, filters] });
        }
        throw error;
      }
    },
    getNextPageParam: (lastPage: any) => {
      const responseObj = Array.isArray(lastPage.response) ? lastPage.response[0] : lastPage.response;
      const pageData = responseObj || lastPage;
      const childHasMore = pageData?.childHasMore ?? pageData?.hasMore ?? false;
      const childNextCursor = pageData?.childNextCursor ?? pageData?.nextCursor ?? null;
      
      const result = (childHasMore && childNextCursor) ? childNextCursor : undefined;
      return result;
    },
    initialPageParam: null as string | null,
    enabled: enabled !== false && Boolean(projectId && taskId && isEditMode),
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
    retry: false,
  });

  const [cachedChildTotalElements, setCachedChildTotalElements] = useState(0);
  const [cachedTotalOpen, setCachedTotalOpen] = useState(0);
  const [cachedTotalInProgress, setCachedTotalInProgress] = useState(0);
  const [cachedTotalDone, setCachedTotalDone] = useState(0);

  useEffect(() => {
    if (!childrenInfiniteData?.pages?.[0]) return;
    const firstPage = childrenInfiniteData.pages[0];
    const pageData = (Array.isArray(firstPage?.response) ? firstPage?.response[0] : firstPage?.response) as any || firstPage;
    const total = pageData?.childTotalElements ?? pageData?.totalElements;
    if (total !== null && total !== undefined) {
      setCachedChildTotalElements(total);
    }
    // Status breakdown counts — provided by the get-children endpoint and scoped
    // to just the children (unlike the parent task's own counts which include itself).
    if (pageData?.totalOpenWorkItemCount !== undefined) setCachedTotalOpen(pageData.totalOpenWorkItemCount);
    if (pageData?.totalInProgressWorkItemCount !== undefined) setCachedTotalInProgress(pageData.totalInProgressWorkItemCount);
    if (pageData?.totalDoneWorkItemCount !== undefined) setCachedTotalDone(pageData.totalDoneWorkItemCount);
  }, [childrenInfiniteData]);

  const [loadingParents, setLoadingParents] = useState<Set<string>>(new Set());

  // --- Pagination Helpers ---
  const firstChildPage = childrenInfiniteData?.pages?.[0];
  const firstChildResponseObj = (Array.isArray(firstChildPage?.response) ? firstChildPage?.response[0] : firstChildPage?.response) as any;
  const page1Data = firstChildResponseObj || firstChildPage;

  const childTotalElements = cachedChildTotalElements;
  const childCurrentPage = childrenInfiniteData?.pages?.length ? childrenInfiniteData.pages.length - 1 : 0;
  const childTotalPages = hasNextChildPage ? childCurrentPage + 2 : childCurrentPage + 1;

  // This task's own listVersion — the version its DIRECT children (the
  // childItems list below) were last read at, required as
  // expectedListVersion when reordering one of them. Read from the most
  // recently loaded page, since that's the freshest value this client has
  // actually seen.
  const lastChildPage = childrenInfiniteData?.pages?.[childrenInfiniteData.pages.length - 1];
  const lastChildResponseObj = (Array.isArray(lastChildPage?.response) ? lastChildPage?.response[0] : lastChildPage?.response) as any;
  const childListVersion: number | undefined = lastChildResponseObj?.listVersion ?? page1Data?.listVersion;

  // --- Derived State: childItems ---
  const childItems = useMemo<ChildItemDraft[]>(() => {
    const allItems = (childrenInfiniteData?.pages || []).flatMap((page: any) => {
      const pObj = Array.isArray(page.response) ? page.response[0] : page.response;
      return pObj?.childWorkItems || pObj?.data || [];
    });
    
    // Deduplicate items by ID to prevent duplicate key errors from stale caches
    const uniqueMap = new Map();
    allItems.forEach((item: any) => {
      const id = item.workItemUuid || item.id;
      if (id && !uniqueMap.has(id)) {
        uniqueMap.set(id, item);
      }
    });
    const uniqueItems = Array.from(uniqueMap.values());
    
    const mapChildItem = (child: any): ChildItemDraft => ({
      id: child.workItemUuid || (child as any).id,
      workItemUuid: child.workItemUuid,
      title: child.title || child.workItemTitle || "",
      workItemKey: child.workItemKey,
      workStatusUuid: child.workStatusUuid,
      workStatusName: child.workStatusName,
      workStatusCategory: child.workStatusCategory,
      workTypeUuid: child.workTypeUuid || null,
      workTypeCategory: child.workTypeCategory || null,
      assigneeUuid: child.workItemAssigneeAppUserUuid || child.assigneeAppUserUuid || null,
      reporterUuid: child.workItemReporterAppUserUuid || child.reporterAppUserUuid || child.reporterUuid || null,
      priority: child.workPriority?.toLowerCase() || child.priority?.toLowerCase() || "medium",
      createdDate: child.createdDate || child.createdAt || "",
      lastModifiedAt: child.lastModifiedAt || child.updatedAt || child.createdDate || child.createdAt || "",
      displayOrder: child.displayOrder,
      totalSubTasksCount: child.totalSubTasksCount || 0,
      totalChildWorkItemsCount: child.totalChildWorkItemsCount || 0,
      totalCommentsCount: child.totalCommentsCount || 0,
      totalMediaCount: child.totalMediaCount || 0,
      totalLoggedTime: child.totalLoggedTime || "0m",
      totalLoggedMinutes: child.totalLoggedMinutes || 0,
      childTotalElements: Math.max(child.childTotalElements || 0, child.childWorkItems?.length || 0),
      childWorkItems: child.childWorkItems ? child.childWorkItems.map(mapChildItem) : [],
      // This child's own listVersion, for ITS children — not the same
      // number as this task's own childListVersion above.
      listVersion: child.listVersion,
    });

    return uniqueItems.map(mapChildItem);
  }, [childrenInfiniteData]);

  useEffect(() => {
    if (childItems.length > pageSizeLimit) {
      setPageSizeLimit(childItems.length);
    }
  }, [childItems.length, pageSizeLimit]);

  // --- Derived State: linkedItems ---
  const linkedItems = useMemo<LinkedItemDraft[]>(() => {
    if (!isEditMode) return localLinkedItems;
    if (!existingTask) return [];
    
    const linkDTOs = (existingTask as any).workItemLinkDTOList;
    if (!linkDTOs || !Array.isArray(linkDTOs)) return [];

    return linkDTOs.map((link: any) => {
      const isSource = link.sourceWorkItemUuid === taskId || link.sourceWorkItemDTO?.workItemUuid === taskId;
      const target = isSource ? link.targetWorkItemDTO : link.sourceWorkItemDTO;
      const otherSideUuid = target?.workItemUuid ||
        (isSource
          ? (link.targetWorkItemUuid || link.targetWorkItemDTO?.workItemUuid)
          : (link.sourceWorkItemUuid || link.sourceWorkItemDTO?.workItemUuid)
        ) || link.workItemLinkUuid;

      return {
        id: otherSideUuid,
        targetWorkItemUuid: link.targetWorkItemUuid || link.targetWorkItemDTO?.workItemUuid || "",
        sourceWorkItemUuid: link.sourceWorkItemUuid || link.sourceWorkItemDTO?.workItemUuid || "",
        linkedWorkItemUuid: link.workItemLinkUuid || "",
        linkType: link.linkType || "RELATED",
        resolvedTask: target
          ? {
              workItemUuid: target.workItemUuid,
              workItemKey: target.workItemKey,
              title: target.title,
              workItemTitle: target.workItemTitle,
              workTypeCategory: target.workTypeCategory,
              workTypeUuid: target.workTypeUuid,
              workTypeName: target.workTypeName,
              workStatusUuid: target.workStatusUuid,
              workStatusName: target.workStatusName,
              workStatusCategory: target.workStatusCategory,
              workItemAssigneeAppUserUuid: target.workItemAssigneeAppUserUuid,
              workPriority: target.workPriority,
            }
          : undefined,
      };
    });
  }, [existingTask, taskId, isEditMode, localLinkedItems]);

  // --- Derived State: attachments ---
  const attachments = useMemo<AttachmentDraft[]>(() => {
    const serverAttachments: AttachmentDraft[] = (() => {
      if (!existingTask) return [];
      const mediaDTOs = (existingTask as any).workItemMediaDTOList;
      if (!mediaDTOs || !Array.isArray(mediaDTOs)) return [];

      return mediaDTOs.map((media: any) => {
        let mimeType = "application/octet-stream";
        if (media.mediaType === "IMAGE") mimeType = "image/png";
        else if (media.mediaType === "VIDEO") mimeType = "video/mp4";
        else if (media.mediaType === "DOCUMENT_PDF") mimeType = "application/pdf";
        else if (media.mediaType === "DOCUMENT_WORD") mimeType = "application/msword";

        return {
          id: media.mediaUuid,
          mediaUuid: media.mediaUuid,
          name: media.originalFileName || "Untitled File",
          size: media.mediaSize || 0,
          type: mimeType,
          url: "", 
          status: "ready" as const,
        };
      });
    })();

    if (!isEditMode) return localAttachments;

    const serverMediaUuids = new Set(serverAttachments.map(a => a.mediaUuid).filter(Boolean));

    const transientAttachments = localAttachments.filter((a: any) => {
      if (a.status !== 'ready') return true;
      return a.mediaUuid && !serverMediaUuids.has(a.mediaUuid);
    });

    return [...serverAttachments, ...transientAttachments];
  }, [existingTask, isEditMode, localAttachments]);


  // --- Actions ---

  /**
   * Walks the ancestor chain from `startParentUuid` upward using
   * cached task data (prefix-matched via getQueriesData) and invalidates
   * both the task detail query and the infinite-children query for every
   * ancestor found.  This ensures that deeply-nested child additions
   * (e.g. Subtask → Task → Story → Epic) propagate the count update
   * all the way to the root without requiring a manual refresh.
   */
  const invalidateAllAncestors = (startParentUuid: string | null | undefined) => {
    if (!startParentUuid) return;
    let currentUuid: string | null | undefined = startParentUuid;
    const visited = new Set<string>();

    while (currentUuid && !visited.has(currentUuid)) {
      visited.add(currentUuid);

      // Invalidate this ancestor's task detail and infinite-children queries
      queryClient.invalidateQueries({ queryKey: ['task', projectId, currentUuid], exact: false });
      queryClient.invalidateQueries({ queryKey: ['task-children-infinite', projectId, currentUuid], exact: false });

      // Climb one more level by reading the ancestor's own parent UUID from cache
      const matches: Array<[unknown, unknown]> = queryClient.getQueriesData<unknown>({ queryKey: ['task', projectId, currentUuid] });
      if (!matches.length) break;
      const cachedData: any = matches[0][1];
      if (!cachedData) break;

      currentUuid = (cachedData as any).parentWorkItemUuid ||
                    (cachedData as any).workItemParentWorkItemUuid ||
                    null;
    }
  };


  const handleExpandChild = useCallback(async (childId: string) => {
    if (!projectId || !taskId || loadingParents.has(childId)) return;
    
    try {
      setLoadingParents((prev: Set<string>) => new Set(prev).add(childId));
      
      let currentTask: Task | undefined;
      const currentCache = queryClient.getQueryData<InfiniteData<any>>([
        'task-children-infinite', projectId, taskId, pageSizeLimit, filters
      ]);
      
      if (currentCache?.pages) {
        const findTaskRecursively = (tasks: Task[]): Task | undefined => {
          for (const t of tasks) {
            if ((t.workItemUuid || (t as any).id) === childId) return t;
            if (t.childWorkItems?.length) {
              const found = findTaskRecursively(t.childWorkItems);
              if (found) return found;
            }
          }
          return undefined;
        };
        const allRaw = currentCache.pages.flatMap((p: any) => {
          const pObj = Array.isArray(p.response) ? p.response[0] : p.response;
          return pObj?.childWorkItems || pObj?.data || [];
        });
        currentTask = findTaskRecursively(allRaw);
      }

      const nextCursor = currentTask?.childNextCursor || (currentTask as any)?.nextCursor || null;
      let response;
      try {
        response = await getWorkItemChildren(projectId, childId, nextCursor, 20);
      } catch (err: any) {
        if (err?.status === 400 && nextCursor !== null) {
          response = await getWorkItemChildren(projectId, childId, null, 20);
        } else {
          throw err;
        }
      }
      const childData = (response as any).response || response || {};
      const newSubChildren = childData.childWorkItems || childData.data || [];
      const childNextCursor = childData.childNextCursor || childData.nextCursor || null;
      const childHasMore = childData.childHasMore ?? childData.hasMore ?? false;
      const childTotalElements = childData.childTotalElements ?? childData.totalElements;
      
      queryClient.setQueryData<InfiniteData<any>>(
        ['task-children-infinite', projectId, taskId, pageSizeLimit, filters],
        (old) => {
          if (!old || !old.pages) return old;
          
          const updateTaskRecursively = (tasks: Task[]): Task[] => {
            return tasks.map(task => {
              const currentId = task.workItemUuid || (task as any).id;
              if (currentId === childId) {
                const existingChildren = task.childWorkItems || [];
                const mergedChildren = nextCursor === null ? newSubChildren : [...existingChildren, ...newSubChildren];
                const newTotal = childTotalElements || mergedChildren.length;

                return { 
                  ...task, 
                  childWorkItems: mergedChildren,
                  totalChildWorkItemsCount: newTotal,
                  childTotalElements: newTotal,
                  childNextCursor,
                  childHasMore
                };
              }
              if (task.childWorkItems && task.childWorkItems.length > 0) {
                return { ...task, childWorkItems: updateTaskRecursively(task.childWorkItems) };
              }
              return task;
            });
          };

          return {
            ...old,
            pages: old.pages.map((page: any) => {
              const pObj = Array.isArray(page.response) ? page.response[0] : page.response;
              const updatedChildren = updateTaskRecursively(pObj?.childWorkItems || []);
              
              const newResponse = Array.isArray(page.response)
                ? [{ ...pObj, childWorkItems: updatedChildren }]
                : { ...pObj, childWorkItems: updatedChildren };

              return { ...page, response: newResponse };
            })
          };
        }
      );
    } catch (error) {
      console.error('[Expansion] Failed to fetch sub-children:', error);
    } finally {
      setLoadingParents((prev: Set<string>) => {
        const next = new Set(prev);
        next.delete(childId);
        return next;
      });
    }
  }, [projectId, taskId, loadingParents, queryClient, filters]);

  const handleChildAdd = async (
    childTitle: string,
    workTypeUuid: string,
    childAssigneeUuid: string | null,
    priority: string,
    existingTaskUuid?: string,
    existingTaskData?: any,
  ): Promise<boolean> => {
    if (!projectId || !taskId) return false;

    const oldParentUuid = existingTaskData?.workItemParentWorkItemUuid || existingTaskData?.parentWorkItemUuid;
    const hasExistingParent = !!oldParentUuid;

    return new Promise<boolean>((resolve) => {
      const isLinkingExisting = !!existingTaskUuid && !!existingTaskData;
      const payload = {
        workItemUuid: existingTaskUuid,
        projectUuid: projectId,
        workItemTitle: childTitle,
        workTypeUuid: workTypeUuid,
        workStatusUuid: isLinkingExisting
          ? existingTaskData.workStatusUuid
          : defaultData?.workStatusDTOList?.find((s) => s.workStatusCategory === "TODO")?.workStatusUuid || "",
        workItemReporterAppUserUuid: isLinkingExisting
          ? (existingTaskData.workItemReporterAppUserUuid || authUser?.appUserUuid || null)
          : authUser?.appUserUuid || null,
        workItemAssigneeAppUserUuid: isLinkingExisting
          ? existingTaskData.workItemAssigneeAppUserUuid
          : childAssigneeUuid || null,
        workPriority: isLinkingExisting
          ? (existingTaskData.workPriority || "MEDIUM")
          : (priority.toUpperCase() || "MEDIUM"),
        workItemDescription: isLinkingExisting ? existingTaskData.workItemDescription || "" : "",
        projectLabelUuidList: isLinkingExisting ? existingTaskData.projectLabelUuidList || [] : [],
        workItemParentWorkItemUuid: taskId,
        parentWorkItemUuid: taskId,
      };

      const optimisticItem = {
        workItemUuid: existingTaskUuid || `temp-${Date.now()}`,
        workItemKey: isLinkingExisting ? existingTaskData.workItemKey : "SAVING...",
        title: childTitle,
        workItemTitle: childTitle,
        workStatusUuid: payload.workStatusUuid,
        workStatusName: isLinkingExisting ? existingTaskData.workStatusName : "TO DO",
        workStatusCategory: isLinkingExisting ? existingTaskData.workStatusCategory : "TODO",
        workTypeUuid: workTypeUuid,
        workItemAssigneeAppUserUuid: payload.workItemAssigneeAppUserUuid,
        workItemReporterAppUserUuid: payload.workItemReporterAppUserUuid,
        workPriority: payload.workPriority,
        lastModifiedAt: new Date().toISOString(),
        createdDate: new Date().toISOString(),
        childWorkItems: isLinkingExisting ? (existingTaskData.childWorkItems || []) : [],
      };

      queryClient.setQueryData<InfiniteData<any>>(
        ['task-children-infinite', projectId, taskId, pageSizeLimit, filters],
        (old) => {
          if (!old || !old.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page, idx) => {
              if (idx !== 0) return page;
              const pObj = Array.isArray(page.response) ? page.response[0] : page.response;
              const existingItems = pObj.childWorkItems !== undefined ? pObj.childWorkItems : (pObj.data || []);
              const newItems = [optimisticItem, ...existingItems];
              const newPObj = {
                ...pObj,
                ...(pObj.childWorkItems !== undefined && { childWorkItems: newItems }),
                ...(pObj.data !== undefined && { data: newItems }),
                ...(pObj.childWorkItems === undefined && pObj.data === undefined && { childWorkItems: newItems }),
              };
              const newResponse = Array.isArray(page.response) ? [newPObj] : newPObj;
              return { ...page, response: newResponse };
            })
          };
        }
      );

      childItemMutation(payload as any, {
        onSuccess: (data) => {
          // Refetch the current task's details so totalChildWorkItemsCount updates in the badge
          queryClient.invalidateQueries({ queryKey: ["task", projectId, taskId] });
          queryClient.invalidateQueries({ queryKey: ["tasks"], exact: false });

          // For NEW items: we injected an optimistic item. Update it with the real UUID first.
          // Then delay the server refetch slightly to ensure the backend read replica/index is fully consistent.
          if (!isLinkingExisting) {
            queryClient.setQueryData<InfiniteData<any>>(
              ['task-children-infinite', projectId, taskId, pageSizeLimit, filters],
              (old) => {
                if (!old || !old.pages) return old;
                return {
                  ...old,
                  pages: old.pages.map((page, idx) => {
                    if (idx !== 0) return page;
                    const pObj = Array.isArray(page.response) ? page.response[0] : page.response;
                    const replaceTempId = (items: any[]) => items.map((item: any) => 
                      item.workItemUuid === optimisticItem.workItemUuid ? { ...item, workItemUuid: data.workItemUuid, id: data.workItemUuid } : item
                    );
                    const newPObj = {
                      ...pObj,
                      ...(pObj.childWorkItems !== undefined && { childWorkItems: replaceTempId(pObj.childWorkItems) }),
                      ...(pObj.data !== undefined && { data: replaceTempId(pObj.data) }),
                    };
                    return { ...page, response: Array.isArray(page.response) ? [newPObj] : newPObj };
                  })
                };
              }
            );

            setTimeout(() => {
              queryClient.invalidateQueries({ queryKey: ["task-children-infinite", projectId, taskId], exact: false });
            }, 1500);
          }

          if (hasExistingParent && oldParentUuid) {
            queryClient.invalidateQueries({ queryKey: ['task-children-infinite', projectId, oldParentUuid], exact: false });
            queryClient.invalidateQueries({ queryKey: ['task', projectId, oldParentUuid] });
            toast.success("Work item moved to this parent successfully");
          } else {
            toast.success(isLinkingExisting ? "Child item added successfully" : "Child item created successfully");
          }

          // Walk the full ancestor chain and invalidate every cached ancestor.
          // This ensures deeply-nested additions (level 2+) propagate the updated
          // totalChildWorkItemsCount all the way up to the root without a manual refresh.
          const grandparentUuid =
            (existingTask as any)?.parentWorkItemUuid ||
            (existingTask as any)?.workItemParentWorkItemUuid;
          invalidateAllAncestors(grandparentUuid);

          resolve(true);
        },
        onError: (err: any) => {
          console.error("Failed to save child task", err);
          const errorMsg = err?.response?.data?.message || err?.message || "Failed to save child task";
          toast.error(errorMsg);
          resolve(false);
        },
      });
    });
  };

  const handleUpdateChildItem = async (childTaskId: string, updates: Partial<any>) => {
    if (!projectId || !childTaskId) return;
    
    const allRawChildren = childrenInfiniteData?.pages.flatMap((p: any) => {
      const pObj = Array.isArray(p.response) ? p.response[0] : p.response;
      return pObj?.childWorkItems || pObj?.data || [];
    }) || [];

    let originalTask: any;
    let parentUuid: string | undefined; // Initialize as undefined
    
    const findRecursive = (list: any[], currentParentUuid: string | undefined): boolean => {
      for (const item of list) {
        if (item.workItemUuid === childTaskId) {
          originalTask = item;
          parentUuid = currentParentUuid;
          return true;
        }
        if (item.childWorkItems && item.childWorkItems.length > 0) {
          if (findRecursive(item.childWorkItems, item.workItemUuid)) return true;
        }
      }
      return false;
    };
    
    // Start search using taskId as the initial parent context
    findRecursive(allRawChildren, taskId ?? undefined);

    if (!originalTask) {
       console.warn("[useTaskDetailManagement] Could not find nested task in cache:", childTaskId);
    }

    // --- OPTIMISTIC UPDATE ---
    const updateRecursiveState = (list: any[]): any[] => {
      return list.map((item: any) => {
        if (item.workItemUuid === childTaskId) {
          return {
            ...item,
            ...updates,
            lastModifiedAt: new Date().toISOString(),
            workPriority: updates.workPriority ? updates.workPriority.toUpperCase() : item.workPriority
          };
        }
        if (item.childWorkItems && item.childWorkItems.length > 0) {
          return {
            ...item,
            childWorkItems: updateRecursiveState(item.childWorkItems),
          };
        }
        return item;
      });
    };

    queryClient.setQueryData<InfiniteData<any>>(
      ['task-children-infinite', projectId, taskId, pageSizeLimit, filters],
      (old) => {
        if (!old || !old.pages) return old;
        return {
          ...old,
          pages: old.pages.map(page => {
            const pObj = Array.isArray(page.response) ? page.response[0] : page.response;
            const updatedChildren = updateRecursiveState((pObj as any)?.childWorkItems || []);
            const newResponse = Array.isArray(page.response)
                ? [{ ...pObj, childWorkItems: updatedChildren }]
                : { ...pObj, childWorkItems: updatedChildren };
            return { ...page, response: newResponse };
          })
        };
      }
    );

    // Resolve the final parent UUID with a robust fallback chain
    const finalParentUuid = 
      updates.parentWorkItemUuid || 
      updates.workItemParentWorkItemUuid || 
      parentUuid || 
      originalTask?.workItemParentWorkItemUuid || 
      originalTask?.parentWorkItemUuid || 
      (originalTask?.workTypeCategory === 'SUB_TASK' ? undefined : taskId) || // Don't guess taskId if it's a sub-task
      null;

    // Look up status metadata to ensure consistency
    const newStatusUuid = updates.workStatusUuid || originalTask?.workStatusUuid;
    const statusMetadata = defaultData?.workStatusDTOList?.find(s => s.workStatusUuid === newStatusUuid);

    const payload = {
      projectUuid: projectId,
      workItemUuid: childTaskId,
      ...(updates.workTypeUuid !== undefined ? { workTypeUuid: updates.workTypeUuid } : {}),
      workItemTitle: updates.workItemTitle || updates.title || originalTask?.title || originalTask?.workItemTitle || "",
      title: updates.workItemTitle || updates.title || originalTask?.title || originalTask?.workItemTitle || "",
      ...(updates.workStatusUuid !== undefined ? { 
        workStatusUuid: updates.workStatusUuid,
        workStatusCategory: statusMetadata?.workStatusCategory,
        workStatusName: statusMetadata?.name,
      } : {}),
      ...(updates.workPriority !== undefined ? { workPriority: updates.workPriority.toUpperCase() } : {}),
      ...(updates.workItemAssigneeAppUserUuid !== undefined ? { workItemAssigneeAppUserUuid: updates.workItemAssigneeAppUserUuid } : {}),
      ...(updates.workItemReporterAppUserUuid !== undefined ? { workItemReporterAppUserUuid: updates.workItemReporterAppUserUuid || "" } : {}),
      workItemDescription: updates.workItemDescription ?? originalTask?.workItemDescription ?? originalTask?.description ?? "",
      projectLabelUuidList: updates.projectLabelUuidList || originalTask?.projectLabelUuidList || [],
      ...(updates.parentWorkItemUuid !== undefined || updates.workItemParentWorkItemUuid !== undefined ? {
        workItemParentWorkItemUuid: updates.parentWorkItemUuid || updates.workItemParentWorkItemUuid || "",
        parentWorkItemUuid: updates.parentWorkItemUuid || updates.workItemParentWorkItemUuid || "",
      } : {}),
    };

    const invalidateChildren = () =>
      queryClient.invalidateQueries({ queryKey: ['task-children-infinite', projectId, taskId], exact: false });

    childItemMutation(payload as any, { 
      onSuccess: invalidateChildren, 
      onError: invalidateChildren 
    });
  };

  const handleDeleteChild = async (childTaskId: string): Promise<boolean> => {
    if (!projectId || !childTaskId) return false;
    return new Promise<boolean>((resolve) => {
      deleteTask(
        { projectUuid: projectId, workItemUuid: childTaskId },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["task", projectId, taskId] });
            queryClient.resetQueries({ queryKey: ["tasks"], exact: false });
            queryClient.invalidateQueries({ queryKey: ["task-children-infinite", projectId, taskId], exact: false });

            // Walk the full ancestor chain so the count propagates to the root.
            const grandparentUuid =
              (existingTask as any)?.parentWorkItemUuid ||
              (existingTask as any)?.workItemParentWorkItemUuid;
            invalidateAllAncestors(grandparentUuid);

            resolve(true);
          },
          onError: (err: any) => {
            console.error("Failed to delete child task", err);
            resolve(false);
          },
        }
      );
    });
  };

  return {
    existingTask,
    isFetchingTask,
    childItems,
    linkedItems,
    attachments,
    
    // Pagination
    fetchNextChildPage,
    hasNextChildPage,
    isFetchingMoreChildren,
    isLoadingInitialChildren,
    childTotalElements,
    childCurrentPage,
    childTotalPages,
    childListVersion,

    // Status counts sourced from get-children (correct — excludes parent itself)
    totalOpenWorkItemCount: cachedTotalOpen,
    totalInProgressWorkItemCount: cachedTotalInProgress,
    totalDoneWorkItemCount: cachedTotalDone,
    
    loadingParents,
    handleExpandChild,
    handleChildAdd,
    handleUpdateChildItem,
    handleDeleteChild,
    
    // Setters for local unsaved state
    setLocalLinkedItems,
    setLocalAttachments,
    
    // Clear state when navigating between tasks
    resetLocalState: useCallback(() => {
      setLocalLinkedItems([]);
      setLocalAttachments([]);
    }, []),
  };
};
